import React, { useEffect } from 'react';

import { connect } from 'react-redux';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';

import { ListItemText } from '@material-ui/core';
import { AutocompleteRenderOptionState } from '@material-ui/lab';

import {
  EMPLOYEE_STATUS,
  loadOrgEmployeesByStatus,
} from '../employees/actions/load-org-employees-by-status';
import {
  employeesForOrgIdSelector,
  employeesStateForIdSelector,
} from '../reducers/employee-selectors';

import { UiAutocomplete } from '../ui/ui-autocomplete';

import { employeeName, employeeNameWithNickname } from '../employees/utils/employee-name';
import { convertArrayToMap } from '../util/convert-array-to-map';
import { formatPhoneNumber } from '../util/formatters';

type InputProps = {
  label?: string;
  orgId: number;
  value: number | null;
  onChange: (employeeId: number | null) => void;
  autoFocus?: boolean;
  excludeEmployeeIds?: Array<number>;
  renderOption?: (
    option: SelectOption<string>,
    state: AutocompleteRenderOptionState,
  ) => React.ReactNode;
};

const mapStateToProps = (state: any, { orgId }: InputProps) => ({
  employees: employeesForOrgIdSelector(state, orgId),
  employeesState: employeesStateForIdSelector(state, orgId),
});

const mapDispatchToProps = (
  dispatch: ThunkDispatch<any, any, AnyAction>,
  { orgId }: InputProps,
) => ({
  actions: {
    loadEmployees: () => dispatch(loadOrgEmployeesByStatus(orgId, EMPLOYEE_STATUS.CURRENT)),
  },
});

type ReduxProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ReturnType<typeof mapDispatchToProps>;
type Props = InputProps & ReduxProps & DispatchProps;

export const EmployeeSelector = connect(
  mapStateToProps,
  mapDispatchToProps,
)(
  ({
    employees,
    value,
    onChange,
    actions,
    label,
    autoFocus,
    excludeEmployeeIds,
    renderOption,
  }: Props) => {
    useEffect(() => {
      actions.loadEmployees();
    });

    if (!employees) {
      return null;
    }

    const employeesById = convertArrayToMap(employees, (e) => e.employeeId.toString());

    const handleOptionClick = (newValue: string | null) => {
      const selectedEmployeeId = newValue ? Number.parseInt(newValue, 10) : null;
      onChange(selectedEmployeeId);
    };

    const defaultRenderOption = (option: SelectOption<string>) => {
      const employee = employeesById[option.value];
      const [hasPhoneNumber, hasEmail] = [
        Boolean(employee.Contact.Person.phoneNumber),
        Boolean(employee.Contact.Person.email),
      ];
      const secondary = (
        <span className="u-display-flex u-justifyContent-spaceBetween">
          {hasPhoneNumber && (
            <div className="u-flexGrow-1 typo-subtitle u-whiteSpace-nowrap u-padding-right-lg">
              {formatPhoneNumber(employee.Contact.Person.phoneNumber)}
            </div>
          )}
          {hasEmail && (
            <div className="u-flexGrow-1 typo-subtitle u-textAlign-right u-overflowWrap-breakWord u-overflow-hidden">
              {employee.Contact.Person.email}
            </div>
          )}
        </span>
      );
      return (
        <ListItemText
          disableTypography
          primary={employeeNameWithNickname(employee)}
          secondary={secondary}
        />
      );
    };

    const options: Array<SelectOption<string>> = employees
      .filter((e) => !excludeEmployeeIds?.includes(e.employeeId))
      .map((e) => ({
        label: employeeName(e),
        value: `${e.employeeId}`,
      }));

    options.sort((a, b) => a.label.toLowerCase().localeCompare(b.label.toLowerCase()));

    const autocompleteValue = `${value || ''}`;

    return (
      <UiAutocomplete
        label={label}
        options={options}
        value={autocompleteValue}
        onChange={handleOptionClick}
        className="u-width-100"
        autoFocus={autoFocus}
        renderOption={renderOption ?? defaultRenderOption}
      />
    );
  },
);
